// settingsStore.js — Praxis-Stammdaten & Behandlerliste.
//
// Bisher waren Praxisname und Behandler hartcodiert ("Dr. Sandra Hoffmann"
// & Kollegen) — für eine echte Praxis unbrauchbar. Jetzt: konfigurierbar
// über "Praxis & Behandler" im Benutzermenü (nur Inhaber), gespeichert in
// Supabase (LIVE) bzw. localStorage (DEMO), mit Cache-Spiegel für alle
// synchronen Verbraucher (TermineScreen, metrics, Vorlagen …).
import { supabase, isSupabaseConfigured } from "./supabase";
import { demoFallbacks, serverActive } from "./mode";

const CACHE_KEY = "dida_settings";

// Demo-Standard: die bekannte Beispielpraxis
export const DEFAULT_PRAXIS = { name: "Dr. Sandra Hoffmann", strasse: "Musterstraße 1", plz: "48683", ort: "Ahaus" };
export const DEFAULT_BEHANDLER = [
  { id: "dh",  name: "Dr. Hoffmann", short: "DH",  color: "#0F6E56" },
  { id: "ds",  name: "Dr. Schulz",   short: "DS",  color: "#185FA5" },
  { id: "lbe", name: "Dr. Bergmann", short: "LBe", color: "#7C3AED" },
  { id: "tf",  name: "Dr. Fischer",  short: "TF",  color: "#92400E" },
  { id: "mr",  name: "Maja Roth",    short: "MR",  color: "#166534" },
];

export const BEHANDLER_PALETTE = ["#0F6E56", "#185FA5", "#7C3AED", "#92400E", "#166534", "#BA7517", "#D85A30", "#0369A1"];

const loadCache = () => {
  try { const s = localStorage.getItem(CACHE_KEY); if (s) return JSON.parse(s); } catch {}
  return null;
};
const saveCache = (obj) => { try { localStorage.setItem(CACHE_KEY, JSON.stringify(obj)); } catch {} };

// Synchroner Zugriff (aus dem Cache) — für Render-Pfade und Vorlagen.
// LIVE ohne Einrichtung: neutral leer statt der Demo-Praxis — sonst stünde
// "Dr. Sandra Hoffmann" in Mails und Vorschauen einer echten Praxis.
const NEUTRAL_PRAXIS = { name: "", strasse: "", plz: "", ort: "" };
// Gespeicherte Praxis mit dem alten Demo-Default-Namen gilt im Live-Betrieb
// als "nicht eingerichtet" (frühere Versionen speicherten ihn versehentlich).
const praxisOrNeutral = (p) => {
  if (!p || (isSupabaseConfigured && p.name === DEFAULT_PRAXIS.name)) {
    return demoFallbacks() ? DEFAULT_PRAXIS : NEUTRAL_PRAXIS;
  }
  return p;
};
export const getPraxis = () => praxisOrNeutral(loadCache()?.praxis);
export const getBehandler = () => {
  const b = loadCache()?.behandler;
  if (Array.isArray(b) && b.length) return b;
  // LIVE ohne Konfiguration: Demo-Namen wären falsch → leere Liste,
  // die UI fordert zum Anlegen auf. DEMO: Beispiel-Behandler.
  return demoFallbacks() ? DEFAULT_BEHANDLER : [];
};
// Generischer Zugriff auf weitere Einstellungs-Schlüssel (z.B. jahresziel)
export const getSetting = (key) => loadCache()?.[key];
export const praxisFooter = () => {
  const p = getPraxis();
  return [p.name, p.strasse, [p.plz, p.ort].filter(Boolean).join(" ")].filter(Boolean).join(" · ") || "Praxis noch nicht eingerichtet";
};

// Frisch laden (Server → Cache)
export async function loadSettings() {
  if (!serverActive()) {
    const cache = loadCache() || {};
    saveCache(cache);
    return { praxis: getPraxis(), behandler: getBehandler() };
  }
  const { data, error } = await supabase.from("settings").select("*");
  if (error) throw error;
  const obj = {};
  data.forEach(r => { obj[r.key] = r.value; });
  saveCache(obj);
  return { praxis: praxisOrNeutral(obj.praxis), behandler: obj.behandler || [] };
}

export async function saveSetting(key, value) {
  // LIVE: erst der Server — schlägt er fehl (fehlende Tabelle, keine
  // Berechtigung), bleibt auch der lokale Cache unverändert. Kein
  // "sieht gespeichert aus, ist es aber nicht".
  if (isSupabaseConfigured) {
    const { error } = await supabase.from("settings").upsert({ key, value }, { onConflict: "key" });
    if (error) throw error;
  }
  const cache = loadCache() || {};
  cache[key] = value;
  saveCache(cache);
}

// Kürzel aus dem Namen ("Dr. Anna Weber" → "AW")
export const makeShort = (name) =>
  name.replace(/^Dr\.\s*/i, "").split(/\s+/).filter(Boolean).map(w => w[0]).join("").slice(0, 3).toUpperCase() || "?";
